import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { CalendarIcon } from 'lucide-react';
import React from 'react';

type DatePickerProps = {
  className?: string;
  value: Date | undefined;
  placeholder?: string;
  onChange: React.Dispatch<React.SetStateAction<Date | undefined>>;
  disabled?: boolean;
  minDate?: Date;
  maxDate?: Date;
  isCustom?: boolean;
  colorSelectedDay?: {
    backgroundColor: string;
    color: string;
  };
};

export const DatePicker = ({
  className,
  value,
  onChange,
  placeholder,
  minDate,
  maxDate,
  disabled = false,
  isCustom = false,
  colorSelectedDay,
}: DatePickerProps) => {
  const [isOpen, setIsOpen] = React.useState(false);

  const handleSelect = (selectedDate: Date | undefined) => {
    onChange(selectedDate);
    setIsOpen(false);
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant='outline'
          disabled={disabled}
          className={cn('w-full font-normal', !value && 'text-muted-foreground', className)}
        >
          <div>{value ? format(value, 'PPP') : <span>{placeholder}</span>}</div>
          <CalendarIcon className='ml-auto h-4 w-4 opacity-50' />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className='w-auto p-0'>
        <Calendar
          mode='single'
          captionLayout='dropdown'
          selected={value}
          onSelect={handleSelect}
          fromYear={1960}
          toYear={new Date().getFullYear()}
          defaultMonth={value}
          fromDate={minDate}
          toDate={maxDate}
          disabled={disabled}
          classNames={{
            day_selected: isCustom
              ? 'bg-[var(--selected-day-background)] text-[var(--selected-day-color)] hover:bg-[var(--selected-day-background)] hover:text-[var(--selected-day-color)]'
              : 'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground',
          }}
          style={
            {
              '--selected-day-background': colorSelectedDay?.backgroundColor || '#000',
              '--selected-day-color': colorSelectedDay?.color || '#fff',
            } as React.CSSProperties
          }
        />
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
